/* ============================================================
   Wido Ops — Company calendar: everyone's daily working status
   (present / leave / late / early leave). Visible to all staff.
   Exports window.CalendarScreen.
   ============================================================ */
(function () {
  const { useState, useMemo } = React;
  const D = window.WidoData;
  const AV = window.WidoAvatar;

  // status meta
  const ST = {
    present: { label: "Present", color: "var(--md-primary)", icon: "check_circle" },
    leave: { label: "On leave", color: "#D6341E", icon: "beach_access" },
    late: { label: "Late", color: "#D6A820", icon: "schedule" },
    early: { label: "Early leave", color: "#D65620", icon: "logout" },
    hourly_leave: { label: "Hourly leave", color: "#D62C8E", icon: "directions_walk" },
    overtime: { label: "Overtime", color: "#7b4fd6", icon: "more_time" },
    offsite: { label: "Offsite work", color: "#0f766e", icon: "public" },
    absent: { label: "Absent", color: "#B00020", icon: "event_busy" },
    off: { label: "Weekend", color: "var(--md-on-surface-variant)", icon: "weekend" },
    holiday: { label: "Holiday", color: "var(--md-tertiary)", icon: "celebration" },
    scheduled: { label: "Scheduled", color: "var(--md-outline)", icon: "more_horiz" },
  };
  const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const FULL_DAY = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  function hash(s) { let h = 2166136261; for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function rng(seed) { return function () { seed |= 0; seed = (seed + 0x6D2B79F5) | 0; let t = Math.imul(seed ^ (seed >>> 15), 1 | seed); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; }; }

  // deterministic leave-day set for a person across June 2026
  function leaveDaysFor(pid, n) {
    if (!n) return new Set();
    const r = rng(hash(pid + "|leave"));
    const wk = [];
    for (let d = 1; d <= 30; d++) if ((d - 1) % 7 < 5) wk.push(d);
    for (let i = wk.length - 1; i > 0; i--) { const j = Math.floor(r() * (i + 1)); const t = wk[i]; wk[i] = wk[j]; wk[j] = t; }
    return new Set(wk.slice(0, Math.min(n, wk.length)));
  }

  // build decided/pending events: leave (approved annual leave) + late/early/hourly
  // that were approved OR acknowledged (info-only), plus pending. Everything else on
  // a working day is just "present".
  function leavePortion(pid, day) { const r = rng(hash(pid + "|p" + day))(); return r < 0.7 ? "full" : (r < 0.85 ? "am" : "pm"); }
  function pad2(n) { return String(n).padStart(2, "0"); }
  function todayISO() { const t = new Date(); return t.getFullYear() + "-" + pad2(t.getMonth() + 1) + "-" + pad2(t.getDate()); }
  // How MUCH a late/early/hourly-leave event is — the decision word travels
  // separately, as the entry's `note`, so the two never wrap into each other.
  // Hourly leave shows its clock-out – clock-back-in (the actual matched times
  // when present, else the estimate, prefixed "~"); late/early show minutes.
  function permDetail(p) {
    if (p.type === "hourly_leave") {
      const matched = !!(p.actualOut && p.actualBack);
      const out = p.actualOut || p.estOut;
      const back = p.actualBack || p.estBack;
      if (out && back) return (matched ? "" : "~") + out + " – " + back;
    }
    // Offsite work: estOut/estBack carry the WINDOW's start/end clocks.
    if (p.type === "offsite" && p.estOut && p.estBack) {
      return p.estOut + " – " + p.estBack;
    }
    return p.mins ? p.mins + "m" : "";
  }
  // "Full day · 08:30–17:30" / "Morning · 08:30–12:00" — how much of the day a
  // session-shaped event covers, and between which clocks. Shared by leave
  // (approved AND pending) and weekend overtime so a half-day always reads as a
  // half-day, whoever has decided it yet.
  function sessionDetail(portion, sh) {
    if (portion === "am") return window.t("Morning") + " · " + sh.amStart + "–" + sh.amEnd;
    if (portion === "pm") return window.t("Afternoon") + " · " + sh.pmStart + "–" + sh.pmEnd;
    return window.t("Full day") + " · " + sh.amStart + "–" + sh.pmEnd;
  }
  function buildEventMap(settings, stats, year, month) {
    const map = {};
    const daysInMonth = new Date(year, month, 0).getDate();
    const today = todayISO();
    (stats || []).forEach((s) => {
      const recByDay = {}; (s.records || []).forEach((r) => { recByDay[r.day] = r; });
      // API mode: real approved-leave ISO dates from the backend. Mock mode: a
      // deterministic fabricated set keyed off the leave-day count.
      const realLeave = s.leaveDates ? new Set(s.leaveDates) : null;
      const mockLeave = realLeave ? null : leaveDaysFor(s.person.id, s.leaveDays || 0);
      // Not-yet-approved leave (from pending leave requests) — surfaced too.
      const pendingLeave = new Set(s.pendingLeaveDates || []);
      // Excused (maternity / extended absence) working days -> reason. Rendered
      // "On leave" with the reason, never "Absent" — the whole point of setting
      // a leave period on the person.
      const excused = s.excusedDates || {};
      // Pending late/early/hourly-leave requests, keyed by ISO -> [{type, mins}].
      const pendingPerm = {};
      (s.pendingPerms || []).forEach((p) => { (pendingPerm[p.date] = pendingPerm[p.date] || []).push(p); });
      // Approved late/early/hourly-leave, keyed by ISO -> [{type, mins}]. A day can
      // hold more than one (e.g. an approved late AND an approved early).
      const approvedPerm = {};
      (s.approvedPerms || []).forEach((p) => { (approvedPerm[p.date] = approvedPerm[p.date] || []).push(p); });
      // Acknowledged (info-only) late/early/hourly-leave — a FINAL decision that still
      // affects the timesheet, so shown as a real event (not pending), like approved.
      const acknowledgedPerm = {};
      (s.acknowledgedPerms || []).forEach((p) => { (acknowledgedPerm[p.date] = acknowledgedPerm[p.date] || []).push(p); });
      // "Today" from the app's authoritative (backend-published) clock for the
      // date comparison, plus the browser time-of-day for whether today's shift
      // has started — used to decide "Absent".
      const appToday = (D.CUR_YEAR && D.CUR_MONTH && D.DAYS_ELAPSED)
        ? (D.CUR_YEAR + "-" + pad2(D.CUR_MONTH) + "-" + pad2(D.DAYS_ELAPSED)) : today;
      const nowMin = (function () { const n = new Date(); return n.getHours() * 60 + n.getMinutes(); })();
      const byDay = {};
      for (let day = 1; day <= daysInMonth; day++) {
        const iso = year + "-" + pad2(month) + "-" + pad2(day);
        const wd = (new Date(year, month - 1, day).getDay() + 6) % 7; // 0=Mon .. 6=Sun
        const holLbl = D.helpers.dayOffLabel(iso, settings);
        if (holLbl) { byDay[day] = { key: "holiday", detail: holLbl }; continue; }
        // Approved overtime on a weekend day shows instead of a plain "off" cell.
        const otPortion = (s.overtimeDates || {})[iso];
        if (otPortion && wd >= 5 && !D.helpers.isCompWork(iso, settings)) {
          const shO = D.helpers.shiftFor(iso, settings);
          byDay[day] = { key: "overtime", otPortion: otPortion, detail: window.t("Overtime") + " · " + sessionDetail(otPortion, shO) };
          continue;
        }
        // A compensation working day turns a weekend into a working day.
        if (wd >= 5 && !D.helpers.isCompWork(iso, settings)) { byDay[day] = { key: "off" }; continue; }
        const sh = D.helpers.shiftFor(iso, settings);
        // 1) Annual leave — approved, then pending (a whole-day status).
        const isLeave = realLeave ? realLeave.has(iso) : mockLeave.has(day);
        if (isLeave) {
          // Real leave carries its session (am/pm/full) via leaveOff so half-days
          // render as half; the mock set fabricates a portion.
          const p = realLeave ? ((s.leaveOff || {})[iso] || "full") : leavePortion(s.person.id, day);
          byDay[day] = { key: "leave", detail: sessionDetail(p, sh), days: p === "full" ? 1 : 0.5 };
          continue;
        }
        if (pendingLeave.has(iso)) {
          // Same session line as approved leave, so an undecided leave still says
          // how long it is — only the trailing word differs. A missing portion map
          // (mock data) falls back to a full day, as the backend does.
          const p = (s.pendingLeaveOff || {})[iso] || "full";
          byDay[day] = { key: "leave", pending: true, days: p === "full" ? 1 : 0.5,
            detail: sessionDetail(p, sh), note: window.t("pending approval") };
          continue;
        }
        // Excused leave period (maternity etc.): a whole-day "On leave", labelled
        // with the HR-entered reason (raw — it's free text, not a UI string).
        if (excused[iso] != null) {
          byDay[day] = { key: "leave", excused: true, days: 1,
            detail: window.rawText(excused[iso]) || window.t("On extended leave") };
          continue;
        }
        // 2) Late / early / hourly-leave come ONLY from requests (approved,
        // acknowledged, then pending), never derived from the raw device records. A
        // day can carry several (e.g. an approved late AND an approved early);
        // approved wins over acknowledged wins over pending for the same type.
        const parts = [];
        const seen = {};
        (approvedPerm[iso] || []).forEach((p) => {
          if (seen[p.type]) return; seen[p.type] = 1;
          parts.push({ key: p.type, detail: permDetail(p), note: window.t("approved") });
        });
        (acknowledgedPerm[iso] || []).forEach((p) => {
          if (seen[p.type]) return; seen[p.type] = 1;
          parts.push({ key: p.type, detail: permDetail(p), note: window.t("acknowledged") });
        });
        (pendingPerm[iso] || []).forEach((p) => {
          if (seen[p.type]) return; seen[p.type] = 1;
          parts.push({ key: p.type, pending: true, detail: permDetail(p), note: window.t("pending approval") });
        });
        if (parts.length) {
          const order = { offsite: 0, hourly_leave: 1, late: 2, early: 3 };
          parts.sort((a, b) => (order[a.key] || 0) - (order[b.key] || 0));
          byDay[day] = { key: parts[0].key, parts: parts, detail: parts[0].detail, note: parts[0].note, pending: parts.every((x) => x.pending) };
          continue;
        }
        // 3) No request: present if they clocked in; otherwise ABSENT once the
        // shift has started (a past day, or today past the start time); a day not
        // yet started is just upcoming → scheduled.
        const rec = recByDay[day];
        const hasIn = !!(rec && rec.in && rec.in !== "—");
        if (hasIn) { byDay[day] = { key: "present" }; continue; }
        const a = (sh.amStart || "08:00").split(":");
        const startMin = (+a[0]) * 60 + (+a[1]);
        const started = iso < appToday || (iso === appToday && nowMin >= startMin);
        byDay[day] = { key: started ? "absent" : "scheduled" };
      }
      map[s.person.id] = byDay;
    });
    return map;
  }

  function Avatar({ name, size }) {
    const cls = "avatar" + (size === "sm" ? " avatar--sm" : "");
    return React.createElement("div", { className: cls, style: { background: AV.color(name) } }, AV.initials(name));
  }

  function CalendarScreen({ me, settings }) {
    // include the language so the memoized event details (which call window.t)
    // recompute when the user toggles EN/VI.
    const lang = window.WidoI18n.lang;
    // The app's "current" month — published by bootstrap.js (API mode, live
    // clock) and data.js (mock fallback), so nav bounds track the real period.
    const NOW = { year: D.CUR_YEAR || 2026, month: D.CUR_MONTH || 6 };
    const USE_API = !!window.WIDO_USE_API;
    const [selMonth, setSelMonth] = useState({ year: NOW.year, month: NOW.month });
    const [team, setTeam] = useState("all");
    const [q, setQ] = useState("");
    // The Everyone Calendar is company-wide for ALL users. In API mode we fetch a
    // dedicated status-only feed (every active person; no fund/deduction data) so
    // employees can see each other — D.companyStats / D.ACTIVE are RBAC-scoped (an
    // employee only gets themselves / their team) and would otherwise hide coworkers.
    const [feed, setFeed] = useState(null);
    const isCurrentMonth = selMonth.year === NOW.year && selMonth.month === NOW.month;
    React.useEffect(() => {
      if (!USE_API) { setFeed(null); return; }
      let cancelled = false;
      setFeed(null);
      window.WidoApi.calendar(selMonth.year, selMonth.month)
        .then((res) => { if (!cancelled) setFeed((res && res.stats) || []); })
        .catch(() => { if (!cancelled) setFeed([]); });
      return () => { cancelled = true; };
    }, [selMonth.year, selMonth.month]);
    // Stats AND roster both come from the company-wide feed in API mode; the mock
    // collections back the offline/demo build.
    const stats = USE_API ? (feed || []) : D.companyStats;
    const roster = USE_API ? (feed || []).map((s) => s.person) : D.ACTIVE;
    const statusMap = useMemo(() => buildEventMap(settings, stats, selMonth.year, selMonth.month), [settings, lang, stats, selMonth.year, selMonth.month]);

    const daysInMonth = new Date(selMonth.year, selMonth.month, 0).getDate();
    const firstWd = (new Date(selMonth.year, selMonth.month - 1, 1).getDay() + 6) % 7; // 0=Mon
    const [sel, setSel] = useState(isCurrentMonth ? D.DAYS_ELAPSED : 1); // selected day
    React.useEffect(() => { setSel(isCurrentMonth ? D.DAYS_ELAPSED : 1); }, [selMonth.year, selMonth.month]);
    const monthLabel = D.helpers.MONTH_NAMES[selMonth.month - 1] + " " + selMonth.year;

    // Month navigation is limited to previous / current / next.
    const curIdx = NOW.year * 12 + (NOW.month - 1);
    const selIdx = selMonth.year * 12 + (selMonth.month - 1);
    function stepMonth(delta) {
      const next = selIdx + delta;
      if (next < curIdx - 1 || next > curIdx + 1) return;
      setSelMonth({ year: Math.floor(next / 12), month: (next % 12) + 1 });
    }

    const people = roster.filter((p) => (team === "all" || p.team === team) && (!q || p.name.toLowerCase().includes(q.toLowerCase())));

    // counts per day for the calendar cells (across filtered people). A day with
    // multiple statuses (e.g. late + early) counts toward each.
    function countsForDay(day) {
      const c = { present: 0, leave: 0, offsite: 0, late: 0, early: 0, hourly_leave: 0, overtime: 0, absent: 0, holiday: 0 };
      people.forEach((p) => {
        const ev = (statusMap[p.id] || {})[day];
        if (!ev) return;
        const keys = ev.parts ? ev.parts.map((x) => x.key) : [ev.key];
        keys.forEach((k) => { if (c[k] != null) c[k]++; });
      });
      return c;
    }

    // weeks grid for the selected month (leading blanks for the 1st's weekday).
    const weeks = []; let wk = new Array(firstWd).fill(null);
    for (let day = 1; day <= daysInMonth; day++) { wk.push(day); if (wk.length === 7) { weeks.push(wk); wk = []; } }
    if (wk.length) { while (wk.length < 7) wk.push(null); weeks.push(wk); }

    const selWd = (new Date(selMonth.year, selMonth.month - 1, sel).getDay() + 6) % 7;
    const selISO = selMonth.year + "-" + pad2(selMonth.month) + "-" + pad2(sel);
    // Non-working = a holiday / swapped-off day, or a weekend that ISN'T a
    // compensation working day — the SAME question the grid cell asks (see
    // `weekend` in the cell below). Deriving it from the weekday alone made a
    // comp Saturday read "Weekend — no working hours" here while its own cell
    // showed everyone's status, so the Working Calendar looked ignored.
    // Split rather than using dayOffLabel: a holiday NAME is user-entered (a
    // proper noun — rawText, never auto-translated), while "Compensatory day
    // off" is our own copy and has to translate.
    const selHoliday = D.helpers.holidayOn(selISO, settings);
    const selCompOff = !selHoliday && D.helpers.isCompOff(selISO, settings);
    const selWeekend = !!selHoliday || selCompOff
      || (selWd >= 5 && !D.helpers.isCompWork(selISO, settings));
    // Show only the notable statuses individually; "present" is collapsed to a
    // count below the list (a full team of present rows is just noise).
    const selList = people.map((p) => ({ p, ev: (statusMap[p.id] || {})[sel] || { key: "scheduled" } }))
      .filter((x) => x.ev.key !== "present" && x.ev.key !== "off")
      .sort((a, b) => { const order = { absent: 0, leave: 1, offsite: 2, late: 3, early: 4, hourly_leave: 5, overtime: 6, present: 7, scheduled: 8, off: 9 }; return ((order[a.ev.key] != null ? order[a.ev.key] : 10) - (order[b.ev.key] != null ? order[b.ev.key] : 10)) || a.p.name.localeCompare(b.p.name); });
    const selCounts = countsForDay(sel);

    function Pill({ k, n }) {
      if (!n) return null;
      return React.createElement("span", { className: "row", style: { gap: 3, alignItems: "center" } },
        React.createElement("span", { style: { width: 7, height: 7, borderRadius: "50%", background: ST[k].color } }),
        React.createElement("span", { className: "mono", style: { fontSize: 11, color: "var(--md-on-surface-variant)" } }, n));
    }

    return React.createElement("div", { className: "stack-v", style: { gap: 20 } },
      React.createElement("div", { className: "between wrap" },
        React.createElement("div", null,
          React.createElement("h2", { className: "section-title" }, "Team calendar"),
          React.createElement("p", { className: "muted", style: { margin: "4px 0 0" } }, window.t("Who's present, on leave, late or leaving early — across the company.") + " " + monthLabel + ".")),
        React.createElement("div", { className: "row", style: { gap: 10, flexWrap: "wrap", alignItems: "center" } },
          React.createElement("div", { className: "row", style: { gap: 2, alignItems: "center" } },
            React.createElement("button", { className: "icon-btn", onClick: () => stepMonth(-1), disabled: selIdx <= curIdx - 1, title: window.t("Previous month") }, React.createElement("span", { className: "material-symbols-rounded" }, "chevron_left")),
            React.createElement("span", { className: "mono", style: { minWidth: 96, textAlign: "center", fontWeight: 600 } }, monthLabel),
            React.createElement("button", { className: "icon-btn", onClick: () => stepMonth(1), disabled: selIdx >= curIdx + 1, title: window.t("Next month") }, React.createElement("span", { className: "material-symbols-rounded" }, "chevron_right"))),
          React.createElement("select", { className: "select", style: { height: 40 }, value: team, onChange: (e) => setTeam(e.target.value) },
            React.createElement("option", { value: "all" }, "All teams"),
            D.TEAMS.map((t) => React.createElement("option", { key: t.id, value: t.id }, window.rawText(t.name)))),
          React.createElement("input", { className: "input", style: { height: 40, width: 180 }, placeholder: window.t("Search name…"), value: q, onChange: (e) => setQ(e.target.value) }))),

      // legend
      React.createElement("div", { className: "legend" },
        ["present", "leave", "offsite", "late", "early", "hourly_leave", "overtime", "absent", "holiday"].map((k) => React.createElement("span", { key: k, className: "legend__item" },
          React.createElement("span", { className: "legend__dot", style: { background: ST[k].color } }), window.t(ST[k].label)))),

      React.createElement("div", { className: "cal-wrap" },
        // ---- calendar grid ----
        React.createElement("div", { className: "card card--pad", style: { minWidth: 0 } },
          React.createElement("div", { className: "cal-grid cal-head" }, DAY_NAMES.map((d, i) => React.createElement("div", { key: d, className: "cal-dow" + (i >= 5 ? " is-weekend" : "") }, window.t(d)))),
          weeks.map((week, wi) => React.createElement("div", { key: wi, className: "cal-grid" },
            week.map((day, di) => {
              if (!day) return React.createElement("div", { key: di, className: "cal-cell is-empty" });
              const wd = (new Date(selMonth.year, selMonth.month - 1, day).getDay() + 6) % 7;
              const iso = selMonth.year + "-" + pad2(selMonth.month) + "-" + pad2(day);
              const holLbl = D.helpers.dayOffLabel(iso, settings);
              // "off" = a non-working day: weekend (unless a comp working day),
              // a holiday, or a working day swapped off.
              const weekend = !!holLbl || (wd >= 5 && !D.helpers.isCompWork(iso, settings));
              const isToday = isCurrentMonth && day === D.DAYS_ELAPSED;
              const isSel = day === sel;
              const future = isCurrentMonth ? (day > D.DAYS_ELAPSED) : (selIdx > curIdx);
              const c = countsForDay(day);
              // A weekend with approved overtime renders like a working cell (shows
              // the overtime pill) rather than a greyed "—".
              const weekendCell = weekend && !c.overtime;
              return React.createElement("button", {
                key: di, title: holLbl || undefined, className: "cal-cell" + (weekendCell ? " is-weekend" : "") + (isSel ? " is-sel" : "") + (future ? " is-future" : ""),
                onClick: () => setSel(day),
              },
                React.createElement("div", { className: "cal-num" + (isToday ? " is-today" : "") }, day),
                !weekendCell && React.createElement("div", { className: "cal-pills" },
                  React.createElement(Pill, { k: "leave", n: c.leave }),
                  React.createElement(Pill, { k: "offsite", n: c.offsite }),
                  React.createElement(Pill, { k: "late", n: c.late }),
                  React.createElement(Pill, { k: "early", n: c.early }),
                  React.createElement(Pill, { k: "hourly_leave", n: c.hourly_leave }),
                  React.createElement(Pill, { k: "overtime", n: c.overtime }),
                  React.createElement(Pill, { k: "absent", n: c.absent })),
                weekendCell && React.createElement("div", { className: "cal-off muted" }, "—"));
            }))),
        ),
        // ---- selected-day detail ----
        React.createElement("div", { className: "card card--pad stack-v", style: { gap: 14 } },
          React.createElement("div", null,
            React.createElement("div", { className: "eyebrow" }, window.t(FULL_DAY[selWd])),
            React.createElement("h3", { className: "section-title", style: { fontSize: 20, marginTop: 2 } }, window.WidoI18n.lang === "vi" ? ("Ngày " + sel + " tháng " + selMonth.month) : (D.helpers.MONTH_NAMES[selMonth.month - 1] + " " + sel))),
          (selWeekend && !selCounts.overtime)
            // Say WHY there are no hours. A holiday or a swapped-off day is not a
            // weekend, and calling it one hid the very entry that made it a day off.
            ? React.createElement("div", { className: "empty" },
                React.createElement("span", { className: "material-symbols-rounded" }, (selHoliday || selCompOff) ? "celebration" : "weekend"),
                React.createElement("p", null, selHoliday ? window.rawText(selHoliday)
                  : selCompOff ? window.t("Compensatory day off")
                  : window.t("Weekend — no working hours.")))
            : React.createElement(React.Fragment, null,
                React.createElement("div", { className: "row", style: { gap: 8, flexWrap: "wrap" } },
                  (selWeekend ? ["overtime"] : ["present", "leave", "late", "early"].concat(selCounts.offsite ? ["offsite"] : []).concat(selCounts.hourly_leave ? ["hourly_leave"] : []).concat(selCounts.overtime ? ["overtime"] : []).concat(selCounts.absent ? ["absent"] : []).concat(selCounts.holiday ? ["holiday"] : [])).map((k) => React.createElement("span", { key: k, className: "tag", style: { background: ST[k].color + "22", color: ST[k].color } },
                    React.createElement("span", { className: "material-symbols-rounded" }, ST[k].icon), selCounts[k] + " " + window.t(ST[k].label).toLowerCase()))),
                React.createElement("div", { className: "divider" }),
                React.createElement("div", { className: "stack-v", style: { gap: 0, maxHeight: 480, overflowY: "auto" } },
                  selList.length === 0 && selCounts.present > 0 && React.createElement("p", { className: "muted", style: { fontSize: 13, padding: "8px 0" } }, window.t("Everyone present — nothing to flag.")),
                  selList.map(({ p, ev }) => React.createElement("div", { key: p.id, className: "between", style: { padding: "9px 0", borderBottom: "1px solid var(--md-outline-variant)" } },
                    React.createElement("div", { className: "row", style: { gap: 10, minWidth: 0 } },
                      React.createElement(Avatar, { name: p.name, size: "sm" }),
                      React.createElement("div", { className: "stack-v", style: { gap: 0, minWidth: 0 } },
                        React.createElement("span", { style: { font: "500 13px/1.2 var(--font-sans)" } }, p.name + (p.id === (me && me.id) ? " (you)" : "")),
                        React.createElement("span", { className: "muted", style: { fontSize: 11 } }, window.rawText(D.helpers.teamName(p.team))))),
                    React.createElement("div", { className: "stack-v", style: { gap: 6, alignItems: "flex-end" } },
                      // One tag (+ detail) per status — a late+early day shows both.
                      (ev.parts || [{ key: ev.key, detail: ev.detail, note: ev.note }]).map((pt, i) => React.createElement("div", { key: i, className: "stack-v", style: { gap: 1, alignItems: "flex-end" } },
                        React.createElement("span", { className: "tag", style: { background: ST[pt.key].color + "22", color: ST[pt.key].color } },
                          React.createElement("span", { className: "material-symbols-rounded" }, ST[pt.key].icon), window.t(ST[pt.key].label)),
                        // How long it is, then how it was decided — two unbreakable
                        // chunks in one wrapping row, so a narrow panel breaks
                        // BETWEEN them instead of through "pending approval".
                        (pt.detail || pt.note) && React.createElement("span", { className: "muted mono row", style: { fontSize: 10.5, gap: 4, flexWrap: "wrap", justifyContent: "flex-end" } },
                          pt.detail && React.createElement("span", { style: { whiteSpace: "nowrap" } }, pt.detail),
                          pt.note && React.createElement("span", { style: { whiteSpace: "nowrap" } }, (pt.detail ? "· " : "") + pt.note))))))))),
        ),
      ),
    );
  }

  window.CalendarScreen = CalendarScreen;
})();
